// Pure helpers for the Emple policies overview: the status cards, the "needs attention" buckets, the list query and one
// table row per policy. Nothing here talks to the network.

export const PRODUCT_CODE = 'emplife_plus'
export const PAGE_SIZES = [20, 50, 100]

/** Every status a policy can be in, in the order people care about, with words an agent understands. */
export const STATUS_CARDS = [
  { key: 'Full_Coverage', label: 'Paid up', color: 'success', hint: 'Premium is up to date' },
  { key: 'In_Arrears', label: 'Behind on payments', color: 'warning', hint: 'Missed a payment, still covered' },
  { key: 'Unpaid', label: 'No payment yet', color: 'default', hint: 'Has not paid its first premium' },
  { key: 'Un_Confirmed', label: 'Waiting for first payment', color: 'info', hint: 'Sold, waiting to be confirmed' },
  { key: 'Non_Payment', label: 'Lapsed', color: 'error', hint: 'Stopped paying and is past the grace period' },
  { key: 'Cancelled', label: 'Cancelled', color: 'default', hint: 'Cancelled' },
  { key: 'Not_Taken_Up', label: 'Not taken up', color: 'default', hint: 'The customer never took the cover up' }
]

/** These four always show; the others only when there is something in them. */
export const ALWAYS_SHOWN = ['Full_Coverage', 'In_Arrears', 'Unpaid', 'Non_Payment']

export const statusMeta = key => STATUS_CARDS.filter(s => s.key === key)[0] || { key, label: String(key || '').replace(/_/g, ' '), color: 'default', hint: '' }

// Emplife Plus lapses once arrears reach the product's own lapse period after the due date (30 days today).
const LAPSE_PERIOD_DAYS = 30
const LAPSE_WARNING_DAYS = 7
const RECENTLY_DUE_MIN_DAYS = 5
const RECENTLY_DUE_MAX_DAYS = 10

const isoDay = (now, offset) => {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + offset))
  return d.toISOString().slice(0, 10)
}
const dayStart = (now, offset) => `${isoDay(now, offset)}T00:00:00.000Z`
const dayEnd = (now, offset) => `${isoDay(now, offset)}T23:59:59.999Z`

/** The follow-up lists the collections team works from, as filters on the same policy list. */
export function attentionBuckets(now = new Date()) {
  return [
    {
      key: 'due-today',
      label: 'Due today',
      hint: 'Not paid up and due today',
      params: { status: 'Unpaid,In_Arrears', date_discriminator: 'due_date', from: dayStart(now, 0), to: dayEnd(now, 0) }
    },
    {
      key: 'recently-due',
      label: 'Due 5 to 10 days ago, still unpaid',
      hint: 'The usual follow-up window',
      params: { status: 'Unpaid,In_Arrears', date_discriminator: 'due_date', from: dayStart(now, -RECENTLY_DUE_MAX_DAYS), to: dayEnd(now, -RECENTLY_DUE_MIN_DAYS) }
    },
    {
      key: 'about-to-lapse',
      label: 'About to lapse',
      hint: 'In arrears and within a week of lapsing',
      params: { status: 'In_Arrears', date_discriminator: 'due_date', from: dayStart(now, -LAPSE_PERIOD_DAYS), to: dayEnd(now, -(LAPSE_PERIOD_DAYS - LAPSE_WARNING_DAYS)) }
    }
  ]
}

export const DATE_BY = [
  { value: 'created_at', label: 'Created' },
  { value: 'start_date', label: 'Start date' },
  { value: 'due_date', label: 'Due date' }
]

/** The request for the policy list: one status or one follow-up bucket, plus the search and date filters. */
export function buildQuery({ status = '', bucket = null, search = '', dateBy = 'created_at', from = '', to = '', page = 1, size = 20 }) {
  const q = { product_code: PRODUCT_CODE, page, size }
  if (bucket) return { ...q, ...bucket.params }
  if (status) q.status = status
  if (search.trim()) q.policy_number = search.trim()
  if (from || to) {
    q.date_discriminator = dateBy
    if (from) q.from = `${from}T00:00:00.000Z`
    if (to) q.to = `${to}T23:59:59.999Z`
  }
  return q
}

/** The share of the book, as a whole percent (never 0 for something that exists). */
export function share(count, total) {
  if (!total || !count) return 0
  return Math.max(1, Math.round((100 * count) / total))
}

const principalOf = policy => {
  const entries = (policy && policy.quotation && policy.quotation.policy_schedule_entries) || []
  const entry = entries.filter(e => e && e.dependant && e.dependant.relationship === 'Principal')[0] || entries[0]
  return (entry && entry.dependant) || {}
}

/** One table row per policy. */
export function policyRow(policy) {
  const p = policy || {}
  const principal = principalOf(p)
  const q = p.quotation || {}
  const named = `${principal.first_name || ''} ${principal.last_name || ''}`.trim()
  const detail = q.additional_details || {}
  return {
    guid: p.guid,
    number: p.policy_number || '',
    name: (p.additional_details && p.additional_details.principal_full_names) || named,
    phone: principal.msisdn || '',
    cover: `${detail.care_level || ''} ${detail.cover_type || ''}`.trim(),
    premiumCents: Number(q.total_premium_amount_in_cents) || 0,
    status: p.status,
    owingCents: Number(p.balance_amount_in_cents) || 0,
    due: p.due_date ? String(p.due_date).slice(0, 10) : '',
    created: p.created_at ? String(p.created_at).slice(0, 10) : ''
  }
}

export const money = cents => `GHS ${(cents / 100).toFixed(2)}`

export const ORDERS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' }
]

/**
 * Which server pages hold one table page, and how to cut them. The policy service always returns newest first, so
 * "oldest first" reads the same list from the far end: the window [from, to) counted from the newest, taken from the
 * (at most two) server pages it spans, then turned round.
 */
export function pageWindow({ total, page, size, order }) {
  if (order !== 'oldest') return { pages: [page + 1], from: 0, to: size, reverse: false }
  const to = Math.max(0, total - page * size)
  const from = Math.max(0, to - size)
  if (to <= from) return { pages: [], from: 0, to: 0, reverse: true }
  const first = Math.floor(from / size) + 1
  const last = Math.floor((to - 1) / size) + 1
  const pages = first === last ? [first] : [first, last]
  return { pages, from: from - (first - 1) * size, to: first === last ? to - (first - 1) * size : size + (to - first * size), reverse: true }
}

/** The rows of one table page from the server pages fetched for it. */
export function cutPage(serverPages, window) {
  const all = [].concat(...serverPages)
  const rows = all.slice(window.from, window.to)
  return window.reverse ? rows.reverse() : rows
}
